import { IComparablesTransactionDTO } from "react-migration/lib/typings/comparables/Comparables";
import { LastValueType, ZooplaComparable } from "react-migration/lib/typings/Zoopla";
import { formatToCurrency, getCurrencyFromLicense } from "react-migration/lib/util/numberFormat";
import { MeasurementSystem } from "src/js/stores/user/store";
import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import {
  ftsqToMsq,
  msqToFtsq,
  pricePerFtSqToPricePerMSq,
  pricePerMsqToPricePerFtsq,
} from "src/js/util/units_and_constants";
import { formatDate } from "react-migration/domains/comparables/util/Dates";
import { useMemo } from "react";
import { ZooplaListingsDTO } from "react-migration/domains/comparables/typings/Zoopla";

export enum PriceSource {
  HOMETRACK = "Hometrack",
  HMLR = "HMLR",
}

export const useComparableDetailData = (
  noFullAccess: boolean,
  record?: IComparablesTransactionDTO,
  zooplaData?: ZooplaComparable,
  zooplaListing?: ZooplaListingsDTO
) => {
  const { t } = useTranslation();
  const currency = getCurrencyFromLicense();
  const unitPreference = getMeasurementSystemPreference();
  const isMetric = unitPreference === MeasurementSystem.METRIC;

  const indexedCurrentValue = useMemo(() => {
    return zooplaData?.valuation?.sale?.currentValue
      ? formatToCurrency({
          amount: zooplaData?.valuation?.sale?.currentValue,
          currency: currency,
          maximumFractionDigits: 0,
        })
      : null;
  }, [zooplaData, currency]);

  const indexedCurrentValuePerArea = useMemo(() => {
    return zooplaData?.valuation?.sale?.currentValuePerSqft
      ? formatToCurrency({
          amount: isMetric
            ? pricePerFtSqToPricePerMSq(zooplaData?.valuation?.sale?.currentValuePerSqft)
            : zooplaData?.valuation?.sale?.currentValuePerSqft,
          currency: currency,
          maximumFractionDigits: 0,
        })
      : zooplaData?.valuation?.sale?.currentValue && zooplaData?.attributes?.floorArea
      ? formatToCurrency({
          amount: isMetric
            ? zooplaData?.valuation?.sale?.currentValue / ftsqToMsq(zooplaData?.attributes?.floorArea)
            : zooplaData?.valuation?.sale?.currentValue / zooplaData?.attributes?.floorArea,
          currency: currency,
          maximumFractionDigits: 0,
        })
      : null;
  }, [zooplaData, currency, isMetric]);

  const estimateMarketValue = useMemo(() => {
    return record?.est_price
      ? formatToCurrency({
          amount: record?.est_price,
          currency: record?.currency ?? currency,
          maximumFractionDigits: 0,
        })
      : null;
  }, [record, currency]);

  const estimateMarketValuePerArea = useMemo(() => {
    return record?.est_price_per_square_metre
      ? formatToCurrency({
          amount: isMetric
            ? record?.est_price_per_square_metre
            : pricePerMsqToPricePerFtsq(record?.est_price_per_square_metre),
          currency: currency,
          maximumFractionDigits: 0,
        })
      : record?.est_price && record?.total_floor_area
      ? formatToCurrency({
          amount: isMetric
            ? record?.est_price / record?.total_floor_area
            : record?.est_price / msqToFtsq(record?.total_floor_area),
          currency: currency,
          maximumFractionDigits: 0,
        })
      : null;
  }, [record, currency, isMetric]);

  const indexedOrEstimateValueSource = useMemo(() => {
    return indexedCurrentValue ? PriceSource.HOMETRACK : estimateMarketValue ? PriceSource.HMLR : null;
  }, [indexedCurrentValue, estimateMarketValue]);

  const rentValueFromListing = useMemo(() => {
    return zooplaListing?.listings?.latest?.url?.includes("to-rent")
      ? zooplaListing?.listings?.latest?.pricing?.price
      : null;
  }, [zooplaListing]);

  const lastKnownValue = useMemo(() => {
    return zooplaData?.valuation?.sale?.lastValue
      ? formatToCurrency({
          amount: zooplaData?.valuation?.sale?.lastValue,
          currency: currency,
          maximumFractionDigits: 0,
        })
      : record?.price
      ? formatToCurrency({
          amount: record?.price,
          currency: record?.currency ?? currency,
          maximumFractionDigits: 0,
        })
      : "N/A";
  }, [zooplaData, record, currency]);

  const lastKnownValueSource = useMemo(() => {
    return zooplaData?.valuation?.sale?.lastValue ? PriceSource.HOMETRACK : record?.price ? PriceSource.HMLR : null;
  }, [zooplaData, record]);

  const lastKnownValueDate = useMemo(() => {
    return zooplaData?.valuation?.sale?.lastValue && zooplaData?.valuation?.sale?.lastValueDate
      ? formatDate(zooplaData?.valuation?.sale?.lastValueDate)
      : record?.price && record?.date_of_transfer
      ? formatDate(record.date_of_transfer)
      : null;
  }, [zooplaData, record, zooplaListing]);

  const zooplaSaleValueType = useMemo(() => {
    if (lastKnownValue && lastKnownValueDate && zooplaData?.valuation?.sale?.lastValueType) {
      return zooplaData?.valuation?.sale?.lastValueType === LastValueType.Sale
        ? "Sold"
        : zooplaData?.valuation?.sale?.lastValueType;
    }
    return null;
  }, [zooplaData, lastKnownValue, lastKnownValueDate]);

  const lastKnownValueType = useMemo(() => {
    return zooplaSaleValueType
      ? zooplaSaleValueType
      : record?.price
      ? t("comparables.selection_details.valuation.sale")
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zooplaData, record]);

  const estimateRentalValue = useMemo(() => {
    return noFullAccess
      ? "£000"
      : zooplaData?.valuation?.rent?.currentValue
      ? formatToCurrency({
          amount: zooplaData?.valuation?.rent?.currentValue,
          currency: currency,
          maximumFractionDigits: 0,
        })
      : "N/A";
  }, [zooplaData, currency, noFullAccess]);

  const estimateRentalValuePerArea = useMemo(() => {
    return noFullAccess
      ? "£000"
      : zooplaData?.valuation?.rent?.currentValuePerSqft
      ? formatToCurrency({
          amount: isMetric
            ? pricePerFtSqToPricePerMSq(zooplaData?.valuation?.rent?.currentValuePerSqft)
            : zooplaData?.valuation?.rent?.currentValuePerSqft,
          currency: currency,
          maximumFractionDigits: 0,
        })
      : zooplaData?.valuation?.rent?.currentValue && zooplaData?.attributes?.floorArea
      ? formatToCurrency({
          amount: isMetric
            ? zooplaData?.valuation?.rent?.currentValue / ftsqToMsq(zooplaData?.attributes?.floorArea)
            : zooplaData?.valuation?.rent?.currentValue / zooplaData?.attributes?.floorArea,
          currency: currency,
          maximumFractionDigits: 0,
        })
      : null;
  }, [zooplaData, currency, isMetric, noFullAccess]);

  const lastRentalValue = useMemo(() => {
    return noFullAccess
      ? "£000"
      : rentValueFromListing
      ? formatToCurrency({
          amount: rentValueFromListing,
          currency: currency,
          maximumFractionDigits: 0,
        })
      : zooplaData?.valuation?.rent?.lastValue
      ? formatToCurrency({
          amount: zooplaData?.valuation?.rent?.lastValue,
          currency: currency,
          maximumFractionDigits: 0,
        })
      : "N/A";
  }, [zooplaData, currency, noFullAccess, rentValueFromListing]);

  const lastRentalValueDate = useMemo(() => {
    return noFullAccess
      ? formatDate(new Date().toISOString())
      : rentValueFromListing && zooplaListing?.listings?.latest?.firstPublishedDate
      ? formatDate(zooplaListing.listings.latest.firstPublishedDate)
      : zooplaData?.valuation?.rent?.lastValue && zooplaData?.valuation?.rent?.lastValueDate
      ? formatDate(zooplaData?.valuation?.rent?.lastValueDate)
      : null;
  }, [zooplaData, noFullAccess, rentValueFromListing, zooplaListing]);

  const zooplaRentalValueType = useMemo(() => {
    if (lastRentalValue && lastRentalValueDate && zooplaData?.valuation?.rent?.lastValueType) {
      return zooplaData?.valuation?.rent?.lastValueType === LastValueType.Sale
        ? "Sold"
        : zooplaData?.valuation?.rent?.lastValueType;
    }
    return null;
  }, [zooplaData, lastRentalValue, lastRentalValueDate]);

  const lastRentalValueType = useMemo(() => {
    return noFullAccess
      ? "Valuation"
      : rentValueFromListing
      ? t("comparables.selection_details.valuation.listed")
      : zooplaRentalValueType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRentalValue, lastRentalValueDate, zooplaRentalValueType, noFullAccess, rentValueFromListing]);

  return {
    indexedOrEstimateCurrentValue: indexedCurrentValue ?? estimateMarketValue ?? "N/A",
    indexedOrEstimateCurrentValuePerArea: indexedCurrentValuePerArea ?? estimateMarketValuePerArea ?? null,
    indexedOrEstimateValueSource,

    lastKnownValue,
    lastKnownValueSource,
    lastKnownValueDate,
    lastKnownValueType,

    estimateRentalValue,
    estimateRentalValuePerArea,

    lastRentalValue,
    lastRentalValueDate,
    lastRentalValueType,
  };
};
